import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiClock,
  FiCheckCircle,
  FiMail,
  FiExternalLink,
  FiRefreshCw,
} from "react-icons/fi";
import api, { APP_URL } from "../services/api";

export default function PendingApproval() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";
  const demo = searchParams.get("demo") === "1";

  const [email, setEmail] = useState(initialEmail);
  const [checking, setChecking] = useState(false);
  const [statusInfo, setStatusInfo] = useState(null); // {status, appUrl} | null
  const [notice, setNotice] = useState(null);

  async function checkStatus() {
    if (!email) {
      setNotice("Enter the email you signed up with to check your status.");
      return;
    }
    setChecking(true);
    setNotice(null);
    const res = await api.getSubscriptionStatus(email);
    setChecking(false);

    if (res.ok) {
      setStatusInfo({ status: res.status, appUrl: res.appUrl || APP_URL });
      if (res.status !== "active") {
        setNotice("Your account is still pending approval. Hang tight!");
      }
    } else {
      setNotice(
        res.message ||
          "We couldn't check your status right now. Please try again later."
      );
    }
  }

  const isActive = statusInfo && statusInfo.status === "active";

  if (isActive) {
    return (
      <section className="section-container flex justify-center py-20">
        <div className="card w-full max-w-md p-8 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <FiCheckCircle className="text-emerald-500" size={32} />
          </span>
          <h1 className="mt-5 text-2xl font-bold">You're approved!</h1>
          <p className="mt-2 text-sm text-slate-600">
            Your account is active and ready to go. Welcome to Money Flow Accounting.
          </p>
          <a
            href={statusInfo.appUrl || APP_URL}
            className="btn-primary mt-6 w-full"
            rel="noreferrer"
          >
            Open Your Accounting App <FiExternalLink size={15} />
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="section-container flex justify-center py-20">
      <div className="card w-full max-w-md p-8 text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
          <FiClock className="text-amber-500" size={32} />
        </span>
        <h1 className="mt-5 text-2xl font-bold">Thanks for signing up!</h1>
        <p className="mt-2 text-sm text-slate-600">
          Your account is pending approval.
        </p>

        {demo && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left text-sm text-blue-700">
            Demo mode: the server is unreachable, so this page is showing the
            standard pending-approval experience.
          </div>
        )}

        <div className="mt-6 space-y-3 rounded-xl bg-surface p-5 text-left">
          <p className="flex items-start gap-2.5 text-sm text-slate-600">
            <FiCheckCircle className="mt-0.5 shrink-0 text-primary" size={15} />
            An administrator reviews every new account to keep Money Flow Accounting
            secure.
          </p>
          <p className="flex items-start gap-2.5 text-sm text-slate-600">
            <FiMail className="mt-0.5 shrink-0 text-primary" size={15} />
            You'll receive an email as soon as your account is approved —
            usually within one business day.
          </p>
          <p className="flex items-start gap-2.5 text-sm text-slate-600">
            <FiExternalLink className="mt-0.5 shrink-0 text-primary" size={15} />
            Once approved, you can open your accounting app right from this
            page.
          </p>
        </div>

        {!initialEmail && (
          <input
            type="email"
            className="input-field mt-6"
            placeholder="Email you signed up with"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}

        {notice && (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            {notice}
          </div>
        )}

        <button
          type="button"
          onClick={checkStatus}
          disabled={checking}
          className="btn-primary mt-6 w-full disabled:opacity-60"
        >
          <FiRefreshCw className={checking ? "animate-spin" : ""} size={15} />
          {checking ? "Checking..." : "Check status"}
        </button>
      </div>
    </section>
  );
}
